import type { DefinitionsFromApi, OverrideResultType } from '@reduxjs/toolkit/query'

import { type GetApiConfigurationByUuidApiArg, tmsApiService } from '../tmsApiService'
import type { EnhancedLegacyManCodeResponse, EnhancedTg3ToMaicConversionResponse } from './enhancedTg3Payload'
import type {
  EnhancedConfigTypeInfo,
  EnhancedConfiguration,
  EnhancedConfigurationId,
  EnhancedConfigurationStepData,
  EnhancedGetAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiResponse,
  EnhancedGetAdmintoolMarketsByMarketIdReferenceTrucksApiResponse,
  EnhancedGetProductdataGetviewheadersByMarketIdApiArg,
  EnhancedGetProductdataMarketByMarketIdLanguagesApiArg,
  EnhancedManCode,
  EnhancedMarketInfo,
  EnhancedMarketLanguages,
  EnhancedPostAdmintoolMarketsByMarketIdReferenceTrucksApiResponse,
  EnhancedPostCodeApiResponse,
  EnhancedPostConfigurationByUuidDeselectVariantsApiArg,
  EnhancedPostConfigurationByUuidSelectVariantAndVariantIdApiArg,
  EnhancedPostConfigurationByUuidSelectVariantsApiArg,
  EnhancedPostConfigurationByUuidSelectVariantsApiResponse,
  EnhancedPostConfigurationByUuidStepAndStepApiArg,
  EnhancedPostConfigurationByUuidUndocommitApiArg,
  EnhancedPostConvertEcmConfigByUuid,
  EnhancedPutAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiResponse,
  EnhancedPutConfigurationByUuidChangeGroupOfInterestApiArg,
  EnhancedVehicleTypeInfo,
  EnhancedViewHeaderInfo,
} from './enhancedTmsApiTypes.ts'
import type { OverrideArgumentResultType, UpdateApi, UpdateDefinitions, UpdateRecord } from './utils'

// type TagTypes = TagTypesFromApi<typeof apiService>
type Definitions = DefinitionsFromApi<typeof tmsApiService>

/**
 * These declarations may be incorrect
 * I've tried to enforce as much as I could through types to keep the structure
 */
type FinalDefinitions = {
  postApiConfiguration: OverrideResultType<Definitions['postApiConfiguration'], EnhancedConfiguration>
  getApiConfigurationByUuid: OverrideArgumentResultType<
    Definitions['getApiConfigurationByUuid'],
    UpdateRecord<GetApiConfigurationByUuidApiArg, { uuid: EnhancedConfigurationId }>,
    EnhancedConfigurationStepData
  >
  postApiConfigurationByUuidStepAndStep: OverrideArgumentResultType<
    Definitions['postApiConfigurationByUuidStepAndStep'],
    EnhancedPostConfigurationByUuidStepAndStepApiArg,
    EnhancedConfigurationStepData
  >
  postApiConfigurationByUuidSelectVariants: OverrideArgumentResultType<
    Definitions['postApiConfigurationByUuidSelectVariants'],
    EnhancedPostConfigurationByUuidSelectVariantsApiArg,
    EnhancedPostConfigurationByUuidSelectVariantsApiResponse
  >
  postApiConfigurationByUuidSelectVariantAndVariantId: OverrideArgumentResultType<
    Definitions['postApiConfigurationByUuidSelectVariantAndVariantId'],
    EnhancedPostConfigurationByUuidSelectVariantAndVariantIdApiArg,
    EnhancedConfigurationStepData
  >
  postApiConfigurationByUuidDeselectVariants: OverrideArgumentResultType<
    Definitions['postApiConfigurationByUuidDeselectVariants'],
    EnhancedPostConfigurationByUuidDeselectVariantsApiArg,
    EnhancedConfigurationStepData
  >
  postApiConfigurationByUuidUndocommit: OverrideArgumentResultType<
    Definitions['postApiConfigurationByUuidUndocommit'],
    EnhancedPostConfigurationByUuidUndocommitApiArg,
    EnhancedConfigurationStepData
  >
  getApiProductdataGetvehicletypeids: OverrideResultType<Definitions['getApiProductdataGetvehicletypeids'], EnhancedVehicleTypeInfo>
  getApiProductdataGetconfigurationtypeids: OverrideResultType<Definitions['getApiProductdataGetconfigurationtypeids'], EnhancedConfigTypeInfo>
  getApiProductdataGetviewheadersByMarketId: OverrideArgumentResultType<
    Definitions['getApiProductdataGetviewheadersByMarketId'],
    EnhancedGetProductdataGetviewheadersByMarketIdApiArg,
    EnhancedViewHeaderInfo
  >
  getApiProductdataMarketByMarketIdLanguages: OverrideArgumentResultType<
    Definitions['getApiProductdataMarketByMarketIdLanguages'],
    EnhancedGetProductdataMarketByMarketIdLanguagesApiArg,
    EnhancedMarketLanguages
  >
  getApiUserdataGetmarkets: OverrideResultType<Definitions['getApiUserdataGetmarkets'], EnhancedMarketInfo>
  putApiConfigurationByUuidChangeGroupOfInterest: OverrideArgumentResultType<
    Definitions['putApiConfigurationByUuidChangeGroupOfInterest'],
    EnhancedPutConfigurationByUuidChangeGroupOfInterestApiArg,
    EnhancedConfigurationStepData
  >
  postApiConvertEcmConfigByUuid: OverrideResultType<Definitions['postApiConvertEcmConfigByUuid'], EnhancedPostConvertEcmConfigByUuid>
  postApiCode: OverrideResultType<Definitions['postApiCode'], EnhancedPostCodeApiResponse>
  postApiCodeByIdCopy: OverrideResultType<Definitions['postApiCodeByIdCopy'], EnhancedManCode>
  getApiCodeById: OverrideResultType<Definitions['getApiCodeById'], { code: EnhancedManCode }>

  postApiConvertTg3CodeByLanguage: OverrideResultType<Definitions['postApiConvertTg3CodeByLanguage'], EnhancedTg3ToMaicConversionResponse>
  getApiTg3CodeById: OverrideResultType<Definitions['getApiTg3CodeById'], EnhancedLegacyManCodeResponse>

  getApiAdmintoolMarketsByMarketIdReferenceTrucks: OverrideResultType<
    Definitions['getApiAdmintoolMarketsByMarketIdReferenceTrucks'],
    EnhancedGetAdmintoolMarketsByMarketIdReferenceTrucksApiResponse
  >
  postApiAdmintoolMarketsByMarketIdReferenceTrucks: OverrideResultType<
    Definitions['postApiAdmintoolMarketsByMarketIdReferenceTrucks'],
    EnhancedPostAdmintoolMarketsByMarketIdReferenceTrucksApiResponse
  >
  getApiAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckId: OverrideResultType<
    Definitions['getApiAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckId'],
    EnhancedGetAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiResponse
  >
  putApiAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckId: OverrideResultType<
    Definitions['putApiAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckId'],
    EnhancedPutAdmintoolMarketsByMarketIdReferenceTrucksAndReferenceTruckIdApiResponse
  >
}

export const enhancedTmsApiService = tmsApiService as unknown as UpdateApi<typeof tmsApiService, UpdateDefinitions<Definitions, FinalDefinitions>>
